import React, { useCallback, useState } from 'react'
import { Image, KeyboardAvoidingView, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { LoungeFullModal } from './LoungeFullModal'
import { isConvexErrorWithCode } from '../../utils/errors/convex'
import { translate } from '../../i18n/translate'
import { LOUNGE_FULL_ERROR_CODE } from '@ppwr/convex/lounges/capacity'
import {
  INVALID_JOIN_CODE_ERROR_CODE,
  LOUNGE_INACTIVE_ERROR_CODE,
  LOUNGE_REJOIN_REQUIRES_OWNER_ERROR_CODE
} from '@ppwr/convex/lounges/errors'

function errorMessageIncludes(err: unknown, fragment: string) {
  return err instanceof Error && err.message.toLowerCase().includes(fragment)
}

/**
 * Maps a `joinLoungeByCode` failure to user-facing copy. Never surfaces the raw
 * Convex message; the substring checks cover backends deployed before the error codes existed.
 */
function getJoinErrorMessage(err: unknown): string {
  if (isConvexErrorWithCode(err, INVALID_JOIN_CODE_ERROR_CODE) || errorMessageIncludes(err, 'invalid share code')) {
    return translate('loungeJoinScreen.invalidCode')
  }
  if (
    isConvexErrorWithCode(err, LOUNGE_REJOIN_REQUIRES_OWNER_ERROR_CODE) ||
    errorMessageIncludes(err, 'contact the lounge owner to rejoin')
  ) {
    return translate('loungeJoinScreen.rejoinRequiresOwner')
  }
  if (isConvexErrorWithCode(err, LOUNGE_INACTIVE_ERROR_CODE) || errorMessageIncludes(err, 'not currently active')) {
    return translate('loungeJoinScreen.loungeInactive')
  }
  return translate('loungeJoinScreen.genericError')
}

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backIcon = require('../../../assets/mainHeader/back.png')

export function LoungeJoinScreen() {
  const router = useRouter()
  const { code: codeParam } = useLocalSearchParams<{ code?: string }>()
  const {
    analytics: { setEvent },
    session: { user, isTeacher, isAdmin }
  } = useStore()
  const userId = user?.auth0Id ?? ''
  const mongoUserId = user?._id?.toString()
  const userName = user?.name ?? 'Player'

  const [code, setCode] = useState(codeParam?.toUpperCase() ?? '')
  const [error, setError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [loungeFullModalVisible, setLoungeFullModalVisible] = useState(false)

  const preview = useQuery(
    api.lounges.queries.getLoungeByJoinCode,
    code.length >= 8 ? { joinCode: code.toUpperCase() } : 'skip'
  )

  const joinMutation = useMutation(api.lounges.mutations.joinLoungeByCode)

  const openLoungeFullModal = useCallback(() => {
    setLoungeFullModalVisible(true)
    setError(null)
  }, [])

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: join + Convex error branches
  const handleJoin = useCallback(async () => {
    if (!code.trim()) return
    if (!userId || !mongoUserId) return
    setError(null)

    if (preview?.isFull) {
      openLoungeFullModal()
      return
    }

    setJoining(true)
    try {
      const result = await joinMutation({
        joinCode: code.toUpperCase(),
        userId,
        userName,
        mongoUserId
      })
      setEvent(events.lounge.join_lounge_submitted, {
        userId: userId,
        loungeId: result.loungeId ?? undefined,
        joinCode: code.toUpperCase(),
        isTeacher,
        isAdmin
      })
      router.replace({ pathname: '/lounges/[loungeId]', params: { loungeId: result.loungeId } } as any)
    } catch (err: unknown) {
      if (isConvexErrorWithCode(err, LOUNGE_FULL_ERROR_CODE) || errorMessageIncludes(err, 'lounge is full')) {
        openLoungeFullModal()
      } else {
        setError(getJoinErrorMessage(err))
      }
    } finally {
      setJoining(false)
    }
  }, [
    code,
    userId,
    mongoUserId,
    userName,
    isTeacher,
    isAdmin,
    joinMutation,
    router,
    setEvent,
    preview?.isFull,
    openLoungeFullModal
  ])

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="dark" />

      <View style={styles.header}>
        <Pressable
          onPress={() => (router.canGoBack() ? router.back() : router.push('/lounges' as any))}
          style={styles.backButton}
        >
          <Image source={backIcon} style={styles.backIcon} resizeMode="contain" />
        </Pressable>
        <DS.Text variant="Heading 3/Medium">Join a Lounge</DS.Text>
        <View style={{ width: 40 }} />
      </View>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.body}>
        <DS.Frame gap="16">
          <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            Enter the share code you received from the lounge owner.
          </DS.Text>

          <TextInput
            style={styles.input}
            placeholder="Share code (e.g. AB3XY7K2PQ)"
            placeholderTextColor={DS.palette['Brand/Dark Grey']}
            value={code}
            onChangeText={(t) => {
              setCode(t.toUpperCase())
              setError(null)
            }}
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={10}
          />

          {code.length >= 8 && preview !== undefined && (
            <View style={styles.previewCard}>
              {preview === null ? (
                <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Utility/Error'] }}>
                  {translate('loungeJoinScreen.invalidCode')}
                </DS.Text>
              ) : (
                <DS.Frame gap="12">
                  <DS.Frame row gap="12" style={{ alignItems: 'center' }}>
                    <LoungeAvatar name={preview.name} branding={preview.branding} size={40} />
                    <DS.Frame gap="4">
                      <DS.Text variant="Body Large/Medium">{preview.name}</DS.Text>
                      <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                        {preview.status === 'active'
                          ? translate('loungeJoinScreen.activeLounge')
                          : translate('loungeJoinScreen.loungeUnavailable')}
                      </DS.Text>
                      <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                        {translate('loungeJoinScreen.memberCount', {
                          current: preview.activeMemberCount,
                          max: preview.maxMembers
                        })}
                      </DS.Text>
                    </DS.Frame>
                  </DS.Frame>
                  {preview.isFull && (
                    <DS.Text variant="Body Small/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                      {translate('loungeJoinScreen.atCapacity')}
                    </DS.Text>
                  )}
                </DS.Frame>
              )}
            </View>
          )}

          {error && (
            <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Utility/Error'] }}>
              {error}
            </DS.Text>
          )}

          <DS.Button
            type="On Light"
            variant="Large/Primary"
            backgroundColor="Brand/Maroon"
            onPress={handleJoin}
            disabled={joining || code.length < 8 || preview === null}
          >
            {joining
              ? translate('loungeJoinScreen.joining')
              : preview?.isFull
                ? translate('loungeJoinScreen.loungeFull')
                : translate('loungeJoinScreen.joinLounge')}
          </DS.Button>
        </DS.Frame>
      </KeyboardAvoidingView>

      <LoungeFullModal visible={loungeFullModalVisible} onDismiss={() => setLoungeFullModalVisible(false)} />
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    height: 54
  },
  backButton: {
    padding: 8,
    width: 40
  },
  backIcon: {
    width: 24,
    height: 24
  },
  body: {
    flex: 1,
    padding: 24
  },
  input: {
    height: 52,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    paddingHorizontal: 16,
    backgroundColor: DS.palette['Global/White'],
    fontSize: 18,
    letterSpacing: 2,
    fontWeight: '600'
  },
  previewCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  }
})
